import React, { useState, useEffect } from "react";
import { 
  Award, 
  BookOpen, 
  CheckCircle2, 
  Clock, 
  Flame, 
  Sparkles, 
  ShieldCheck, 
  HelpCircle, 
  Send, 
  Star, 
  ArrowRight, 
  ChevronDown, 
  Play, 
  Volume2, 
  Lock, 
  Stethoscope, 
  Menu, 
  X,
  AlertCircle,
  FileText,
  Users,
  Video
} from "lucide-react";
import { Toaster, toast } from "./components/ui/sonner";
import "@/App.css";

// Testimonial videos from user requirements
const TESTIMONIAL_VIDEOS = [
  {
    id: "JhmRFet3Izs",
    title: "Clinical Potency Case Breakthrough",
    doctor: "Dr. Sneha Patil, BHMS",
    duration: "10-Hour Masterclass Graduate",
    highlight: "Solved my lifelong confusion between 30C and 200C selection in acute pediatric cases.",
    embedUrl: "https://www.youtube.com/embed/JhmRFet3Izs?autoplay=1&mute=0"
  },
  {
    id: "UL6jyiaM5YI",
    title: "Tissue Salts & Biochemics Mastery",
    doctor: "Dr. Amit Sharma, MD Scholar",
    duration: "10-Hour Masterclass Graduate",
    highlight: "The 6X & 12X cell salt combinations matrix doubled my clinical prescription speed.",
    embedUrl: "https://www.youtube.com/embed/UL6jyiaM5YI?autoplay=1&mute=0"
  },
  {
    id: "6_WdP-J82S4",
    title: "Constitutional High-Potency Rules",
    doctor: "Dr. Rashmi Verma, Consulting Homeopath",
    duration: "10-Hour Masterclass Graduate",
    highlight: "Finally understood susceptibility laws before using 1M and 10M potencies safely.",
    embedUrl: "https://www.youtube.com/embed/6_WdP-J82S4?autoplay=1&mute=0"
  },
  {
    id: "2U4rjgFEjAM",
    title: "Avoiding Aggravations in Chronic Cases",
    doctor: "Dr. Vikas Deshmukh, PG Resident",
    duration: "10-Hour Masterclass Graduate",
    highlight: "Dr. Raj's repetition protocol is a life-saver for chronic respiratory prescriptions.",
    embedUrl: "https://www.youtube.com/embed/2U4rjgFEjAM?autoplay=1&mute=0"
  }
];

// Interactive Potency matrix data
const POTENCY_DATA = {
  "6x-12x": {
    title: "Low Potencies & Cell Salts (6X, 12X)",
    subtitle: "Physiological Plane & Cellular Deficiencies",
    description: "Ideal for physical organ support, nutritional deficiencies, and cellular restoration. Taught extensively in Module 2.",
    bullets: [
      "Targeted at the physical structure (tissue level)",
      "High repetition is clinically safe (e.g. 3-4 times daily)",
      "Excellent for pediatric and geriatric cases with low vital force",
      "Key remedies: Calc Phos 6X, Ferrum Phos 12X, Mag Phos 6X"
    ],
    alert: "Never mistake cellular deficiency for dynamic disease. Learn exactly when to switch to dilutions inside our course.",
    color: "from-blue-900/40 to-slate-900/40 border-blue-500/30"
  },
  "30c-200c": {
    title: "Medium Dilutions (30C, 200C)",
    subtitle: "Functional Pathology & Acute Flare-Ups",
    description: "The workhorses of homeopathy. Master the precise boundary between physical ailments and mental-emotional symptoms.",
    bullets: [
      "30C: Local physical symptoms with clear modality",
      "200C: General systemic symptoms & clear emotional traits",
      "Repetition protocol: Acute (every 2-4 hours), Chronic (weekly/single dose)",
      "How to avoid clinical stagnation when a well-selected remedy stops working"
    ],
    alert: "Choosing between 30C and 200C is where 90% of practitioners falter. Module 3 lays down clear mathematical rules.",
    color: "from-amber-950/40 to-slate-900/40 border-amber-500/30"
  },
  "1m-10m": {
    title: "High & Highest Potencies (1M, 10M)",
    subtitle: "Dynamic Constitutional & Miasmatic Prescribing",
    description: "Deep-seated remedies targeting the core mental state, chronic inherited miasms, and highly sensitive patients.",
    bullets: [
      "Requires high susceptibility and clear mental picture",
      "Strict single-dose protocol with dynamic placeholders (placebo)",
      "Unlocking cases stuck due to miasmatic blocks (Anti-Psoric/Syphilitic triggers)",
      "How to handle homeopathic aggravations safely"
    ],
    alert: "Using high potencies blindly can cause devastating clinical aggravations. Module 4 reveals how to assess susceptibility first.",
    color: "from-red-950/40 to-slate-900/40 border-red-500/30"
  },
  "mother-tinctures": {
    title: "Mother Tinctures & Biochemics (Q)",
    subtitle: "Physiological Action & Organ Drainage",
    description: "Direct palliative care, localized clinical drainage, and therapeutic support while the deep constitutional remedy works.",
    bullets: [
      "Immediate physiological organ support (e.g., Liver, Kidney)",
      "Crude botanical and mineral action without high dilution risk",
      "Dosage rules: 5 to 15 drops in lukewarm water",
      "Key clinical mixtures: Crataegus Q, Berberis Vulgaris Q, Chelidonium Q"
    ],
    alert: "Mother Tinctures are highly effective but must be prescribed under strict clinical rules. Module 5 gives you the entire clinical index.",
    color: "from-emerald-950/40 to-slate-900/40 border-emerald-500/30"
  }
};

// 10 recorded lectures syllabus data
const SYLLABUS_MODULES = [
  {
    num: "01",
    title: "Foundations of Homeopathic Posology",
    duration: "60 mins",
    desc: "Understanding susceptibility, vital force response, dynamic vs physiological planes, and the core philosophy behind selecting the exact matching potency.",
    details: ["The law of least action", "Evaluating patient's reaction speed", "Understanding organic vs functional illness"]
  },
  {
    num: "02",
    title: "Low Potencies (6X, 12X) & Tissue Salts",
    duration: "60 mins",
    desc: "Comprehensive guide to Biochemic chemistry, tissue repair, physical organ support, and physical repetition strategies without risk of dynamic aggravations.",
    details: ["The 12 tissue remedies clinic index", "Prescribing combination cell salts", "Physiological deficiency protocols"]
  },
  {
    num: "03",
    title: "Medium Potencies (30C, 200C) in Daily Practice",
    duration: "75 mins",
    desc: "Deep-dive into the two most popular dilutions. Learn how to accurately differentiate their clinical indications in acute infections and chronic constitutional starts.",
    details: ["30C local vs 200C general systemics", "Differentiating repetition in acute emergencies", "Preventing clinical stagnation"]
  },
  {
    num: "04",
    title: "High & Highest Potencies (1M, 10M) Laws",
    duration: "75 mins",
    desc: "Step-by-step masterclass on handling highly diluted remedies. Assess dynamic susceptibility, clear miasmatic blocks, and apply single-dose laws confidently.",
    details: ["When to prescribe 1M vs 10M", "Miasmatic blockages and intercurrent remedies", "How to manage dynamic aggravations"]
  },
  {
    num: "05",
    title: "Mother Tinctures (Q) Clinical Therapeutics",
    duration: "60 mins",
    desc: "Expert guidelines on using crude tinctures for localized clinical organ drainage, direct palliative care, and safe physiological symptom management.",
    details: ["Dr. Raj's top 15 clinical mother tinctures", "Intercurrent drainage during chronic treatment", "Dosage & repetition protocols"]
  },
  {
    num: "06",
    title: "Biochemics Combination Matrix",
    duration: "60 mins",
    desc: "Unlocking advanced biochemical combinations. Learn how to mix and match tissue salts for complex clinical pathologies like chronic joint pains, anemia, and insomnia.",
    details: ["Developing custom biochemic formulas", "Alternating tissue salts systematically", "Clinical case examples"]
  },
  {
    num: "07",
    title: "The Repetition & Post-Prescription Protocol",
    duration: "60 mins",
    desc: "Never struggle with 'What next?' again. Master Kent's 12 Observations, identify true homeopathic improvement, and know exactly when to wait and when to repeat.",
    details: ["Identifying true curative action vs suppression", "Placebo (Saccharum Lactis) administration", "Handling complex remedy relationships"]
  },
  {
    num: "08",
    title: "Case Refractory Protocols & Intercurrents",
    duration: "60 mins",
    desc: "How to salvage cases that have hit a dead-end. Learn the clinical application of anti-psoric, anti-sycotic, and anti-syphilitic intercurrent remedies.",
    details: ["Identifying miasmatic dominance in stagnant cases", "Sulphur, Psorinum, Medorrhinum & Thuja as intercurrents", "Restoring remedy sensitivity"]
  },
  {
    num: "09",
    title: "Clinical Case Studies & Common Posology Errors",
    duration: "65 mins",
    desc: "Analysis of real clinical case files from Dr. Raj's own practice. Learn from common posology blunders made by BHMS graduates and how to correct them.",
    details: ["Analysis of 5 cured chronic cases", "Post-aggravation rescue protocols", "Key mistakes to avoid in pediatric cases"]
  },
  {
    num: "10",
    title: "Advanced Clinical Q&A & Practice Mastery",
    duration: "65 mins",
    desc: "Final master lecture answering critical clinical questions gathered from hundreds of practitioners on complex disease management and high-volume prescribing.",
    details: ["Answering real-time practitioner questions", "Managing patients with heavy patholgoical damage", "Final clinical pearls of Dr. Raj Bondre"]
  }
];

export default function App() {
  // Checkout URL from design guidelines / requirements
  const CHECKOUT_URL = "https://learn.homeomonk.com/web/checkout/68cd2a17a918b1f20c3560d0";

  // State for mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // State for countdown timer
  const [timeLeft, setTimeLeft] = useState(872); // 14 mins 32 secs default

  // State for interactive potency decision widget
  const [activePotencyTab, setActivePotencyTab] = useState("6x-12x");

  // State for syllabus accordion (module index)
  const [expandedModule, setExpandedModule] = useState(0);

  // State for video testimonials custom video embed player
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [isPlayingTestimonial, setIsPlayingTestimonial] = useState(false);

  // State for inquiry form
  const [inquiryName, setInquiryName] = useState("");
  const [inquiryEmail, setInquiryEmail] = useState("");
  const [inquiryPhone, setInquiryPhone] = useState("");
  const [inquirySubmitted, setInquirySubmitted] = useState(false);

  // Timer countdown hook
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 1 ? prev - 1 : 900)); // Reset back to 15 mins for urgency
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  // Inquiry form submit
  const handleInquirySubmit = (e) => {
    e.preventDefault();
    if (!inquiryName || !inquiryEmail || !inquiryPhone) {
      toast.error("Please fill in all clinical registration fields!");
      return;
    }
    setInquirySubmitted(true);
    toast.success("Syllabus Inquiry Submitted! Dr. Raj's team will WhatsApp you shortly.");
  };

  // Helper to scroll to element
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#0A101D] text-[#F8FAFC] font-sans antialiased selection:bg-[#D4AF37] selection:text-slate-950">
      <Toaster position="top-center" closeButton richColors />

      {/* STICKY ANNOUNCEMENT BAR */}
      <div 
        data-testid="announcement-bar"
        className="sticky top-0 z-50 bg-[#111B31] border-b border-[#D4AF37]/30 text-center py-2.5 px-4 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium"
      >
        <span className="inline-flex h-2 w-2 rounded-full bg-red-500 animate-pulse"></span>
        <span className="text-slate-300">SPECIAL CLINICAL LAUNCH OFFER:</span>
        <span className="text-[#D4AF37] font-bold">Save 80% Off!</span>
        <span className="text-slate-400 line-through">₹5,000</span>
        <span className="text-white font-bold bg-[#D4AF37]/20 px-2 py-0.5 rounded border border-[#D4AF37]/30">₹999 Only</span>
        <a
          href={CHECKOUT_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="navbar-enroll-cta"
          className="ml-2.5 inline-flex items-center gap-1 text-[10px] sm:text-xs text-slate-950 font-bold bg-[#D4AF37] hover:bg-[#E5C158] px-2.5 py-1 rounded-full transition-all duration-300 transform hover:scale-105"
        >
          Claim Offer <ArrowRight className="h-3 w-3" />
        </a>
      </div>

      {/* FLOATING HEADER / NAVBAR */}
      <header className="sticky top-11 z-40 bg-[#0A101D]/90 backdrop-blur-md border-b border-[#182542] h-20 transition-all">
        <div className="max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          
          {/* LOGO */}
          <div 
            onClick={() => scrollToSection("hero")}
            className="flex items-center gap-2.5 cursor-pointer group"
            data-testid="navbar-logo"
          >
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8860B] p-0.5 shadow-lg group-hover:rotate-6 transition-all duration-300 flex items-center justify-center">
              <Stethoscope className="h-5 w-5 text-slate-950" />
            </div>
            <div>
              <span className="block text-lg font-bold tracking-tight text-white font-serif group-hover:text-[#D4AF37] transition-colors">
                Dr. Raj Bondre
              </span>
              <span className="block text-[10px] text-[#D4AF37] tracking-wider uppercase font-semibold">
                Homeopathy Studio
              </span>
            </div>
          </div>

          {/* DESKTOP NAV LINKS */}
          <nav className="hidden lg:flex items-center gap-8 text-sm font-medium text-slate-300">
            <button 
              onClick={() => scrollToSection("dilemma")} 
              className="hover:text-[#D4AF37] transition-colors"
              data-testid="nav-link-dilemma"
            >
              The Dilemma
            </button>
            <button 
              onClick={() => scrollToSection("potency-guide")} 
              className="hover:text-[#D4AF37] transition-colors"
              data-testid="nav-link-potency-guide"
            >
              Potency Guide
            </button>
            <button 
              onClick={() => scrollToSection("curriculum")} 
              className="hover:text-[#D4AF37] transition-colors"
              data-testid="nav-link-curriculum"
            >
              10-Hour Syllabus
            </button>
            <button 
              onClick={() => scrollToSection("testimonials")} 
              className="hover:text-[#D4AF37] transition-colors"
              data-testid="nav-link-testimonials"
            >
              Cured Cases Reviews
            </button>
            <button 
              onClick={() => scrollToSection("faq")} 
              className="hover:text-[#D4AF37] transition-colors"
              data-testid="nav-link-faq"
            >
              FAQs
            </button>
          </nav>

          {/* RIGHT ACTION BUTTON */}
          <div className="hidden lg:flex items-center gap-4">
            <div className="text-right text-xs">
              <span className="block text-slate-400">Enrolling now:</span>
              <span className="text-[#D4AF37] font-mono font-bold animate-pulse">42 Doctors active</span>
            </div>
            <a 
              href={CHECKOUT_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="header-checkout-cta"
              className="bg-transparent hover:bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37] hover:border-[#E5C158] px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 hover:shadow-[0_0_15px_rgba(212,175,55,0.15)]"
            >
              Secure Access
            </a>
          </div>

          {/* MOBILE MENU BUTTON */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-slate-400 hover:text-white transition-colors"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* MOBILE DROPDOWN NAV */}
        {mobileMenuOpen && (
          <div 
            className="lg:hidden bg-[#111B31] border-b border-[#182542] px-4 pt-4 pb-6 space-y-3 absolute w-full left-0 shadow-2xl transition-all"
            data-testid="mobile-menu"
          >
            <button 
              onClick={() => scrollToSection("dilemma")} 
              className="block w-full text-left py-2 px-3 rounded-md text-slate-300 hover:bg-[#182542] hover:text-white"
            >
              The Clinical Dilemma
            </button>
            <button 
              onClick={() => scrollToSection("potency-guide")} 
              className="block w-full text-left py-2 px-3 rounded-md text-slate-300 hover:bg-[#182542] hover:text-white"
            >
              Interactive Potency Guide
            </button>
            <button 
              onClick={() => scrollToSection("curriculum")} 
              className="block w-full text-left py-2 px-3 rounded-md text-slate-300 hover:bg-[#182542] hover:text-white"
            >
              10-Hour Syllabus
            </button>
            <button 
              onClick={() => scrollToSection("testimonials")} 
              className="block w-full text-left py-2 px-3 rounded-md text-slate-300 hover:bg-[#182542] hover:text-white"
            >
              Student Testimonials
            </button>
            <button 
              onClick={() => scrollToSection("faq")} 
              className="block w-full text-left py-2 px-3 rounded-md text-slate-300 hover:bg-[#182542] hover:text-white"
            >
              FAQ Accordion
            </button>
            <div className="pt-2 border-t border-[#182542]">
              <a 
                href={CHECKOUT_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-checkout-cta"
                className="block text-center w-full py-3 px-4 rounded-lg bg-[#D4AF37] hover:bg-[#E5C158] text-slate-950 font-bold transition-all duration-300"
              >
                Enroll Now @ ₹999 Only
              </a>
            </div>
          </div>
        )}
      </header>

      {/* HERO SECTION */}
      <section id="hero" className="relative py-16 sm:py-24 lg:py-32 overflow-hidden border-b border-[#182542]">
        
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0 opacity-15">
          <img 
            src="https://images.unsplash.com/photo-1566240834522-54b929e2f52d?crop=entropy&cs=srgb&fm=jpg&q=85" 
            alt="Homeopathic pharmacy instruments" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A101D] mix-blend-multiply"></div>
        </div>

        {/* Dynamic Glowing Dots in Background */}
        <div className="absolute top-1/4 left-1/4 h-72 w-72 bg-[#D4AF37]/5 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 h-80 w-80 bg-blue-500/5 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* HERO LEFT: TEXT & CALLS-TO-ACTION */}
            <div className="lg:col-span-7 space-y-6 sm:space-y-8">
              
              {/* Academy Badge */}
              <div className="inline-flex items-center gap-2 bg-[#111B31] border border-[#D4AF37]/30 px-3.5 py-1.5 rounded-full shadow-inner">
                <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#D4AF37]">
                  Clinical Posology Academy
                </span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.15] font-serif text-white">
                Never Make a Mistake in Selecting the <span className="text-[#D4AF37] relative">Right Potency</span> Again.
              </h1>

              {/* Sub-headline targeting students/homeopaths */}
              <p className="text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
                The ultimate recorded masterclass designed exclusively for <strong className="text-white">BHMS/MD Students</strong> and <strong className="text-white">Practicing Homeopaths</strong>. Master clinical posology, cure stagnant cases, and prescribe with mathematical certainty from <span className="text-[#D4AF37] font-semibold">6X, 12X to 30, 200, 1M, and 10M</span>.
              </p>

              {/* Key trust indicators / mini grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2">
                <div className="bg-[#111B31]/80 border border-[#182542] p-4 rounded-xl flex items-start gap-2.5">
                  <Clock className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                  <div>
                    <span className="block text-sm font-bold text-white">10 Hours</span>
                    <span className="block text-xs text-slate-400">Recorded Lectures</span>
                  </div>
                </div>
                <div className="bg-[#111B31]/80 border border-[#182542] p-4 rounded-xl flex items-start gap-2.5">
                  <Award className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                  <div>
                    <span className="block text-sm font-bold text-white">Clinical Depth</span>
                    <span className="block text-xs text-slate-400">Dr. Raj's Protocol</span>
                  </div>
                </div>
                <div className="col-span-2 sm:col-span-1 bg-[#111B31]/80 border border-[#182542] p-4 rounded-xl flex items-start gap-2.5">
                  <ShieldCheck className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                  <div>
                    <span className="block text-sm font-bold text-white">Lifetime Access</span>
                    <span className="block text-xs text-slate-400">On TagMango Portal</span>
                  </div>
                </div>
              </div>

              {/* ACTION BLOCKS & OFFERS */}
              <div className="space-y-4 pt-2">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <a 
                    href={CHECKOUT_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="hero-enroll-cta"
                    className="flex-1 sm:flex-none text-center bg-[#D4AF37] hover:bg-[#E5C158] text-slate-950 font-bold px-8 py-4.5 rounded-xl transition-all duration-300 shadow-[0_4px_25px_rgba(212,175,55,0.25)] hover:shadow-[0_4px_30px_rgba(212,175,55,0.4)] transform hover:-translate-y-0.5 flex items-center justify-center gap-2 text-base"
                  >
                    <Flame className="h-5 w-5 animate-pulse text-red-700" />
                    Enroll in Potency Mastery Now @ ₹999
                  </a>
                  
                  <button 
                    onClick={() => scrollToSection("curriculum")}
                    className="text-center border border-slate-700 hover:border-slate-500 bg-[#111B31]/30 text-slate-300 hover:text-white px-6 py-4.5 rounded-xl text-sm font-semibold transition-all duration-200"
                    data-testid="hero-curriculum-link"
                  >
                    View 10 Modules
                  </button>
                </div>

                {/* COUNTDOWN TIMER FOR URGENCY */}
                <div 
                  className="bg-[#111B31]/50 border border-[#D4AF37]/20 p-3 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400 max-w-xl"
                  data-testid="countdown-timer"
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-block px-2 py-0.5 bg-red-950 text-red-400 rounded font-bold animate-pulse text-[10px]">
                      LIMITED SEATS AT ₹999
                    </span>
                    <span>Offer ends soon! Price returning to <strong className="text-white">₹5,000</strong></span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>Closing in:</span>
                    <span className="font-mono text-[#D4AF37] font-bold text-sm bg-slate-950 px-2.5 py-1 rounded border border-[#182542]">
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                </div>
              </div>

            </div>

            {/* HERO RIGHT: MASTERCLASS COVER CARD WITH INTRO SHORTS VIDEO */}
            <div className="lg:col-span-5 relative flex justify-center">
              <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-[#D4AF37] to-amber-700 opacity-20 blur-lg animate-pulse"></div>
              
              <div className="relative w-full max-w-[380px] bg-[#111B31] border-2 border-[#D4AF37]/50 rounded-2xl overflow-hidden shadow-2xl p-4 sm:p-5 space-y-4">
                
                {/* Portrait YouTube Shorts Embed */}
                <div className="relative aspect-[9/16] rounded-xl overflow-hidden border border-[#D4AF37]/20 shadow-inner bg-black">
                  <iframe
                    className="w-full h-full absolute inset-0"
                    src="https://www.youtube.com/embed/-pRhoP8lM9A"
                    title="Dr. Raj Bondre Intro Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    data-testid="hero-intro-shorts-iframe"
                  ></iframe>
                </div>

                {/* Key clinical highlights */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b border-[#182542] pb-2">
                    <span className="text-[10px] text-slate-400 font-mono">INTRODUCTORY LECTURE:</span>
                    <span className="text-[10px] bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-0.5 rounded border border-[#D4AF37]/30 font-semibold uppercase font-mono">
                      Watch Preview
                    </span>
                  </div>

                  {/* Badges of potency chips */}
                  <div className="flex flex-wrap gap-1.5">
                    {["6X", "12X", "30C", "200C", "1M", "10M", "Mother Tinctures Q", "Biochemics"].map((chip) => (
                      <span 
                        key={chip} 
                        className="px-2 py-0.5 bg-slate-950/60 rounded border border-slate-800 text-slate-300 text-[9px] font-mono font-medium"
                      >
                        {chip}
                      </span>
                    ))}
                  </div>

                  <ul className="space-y-2 text-[11px] text-slate-400">
                    <li className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" />
                      <span>Learn 6X, 12X to 10M applications</span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" />
                      <span>Precise repetition guidelines</span>
                    </li>
                  </ul>
                </div>

                {/* Action footer inside card */}
                <div className="pt-2 border-t border-[#182542] flex items-center justify-between gap-4">
                  <div>
                    <span className="block text-[9px] text-slate-400 font-medium uppercase tracking-wider">LIMITED LAUNCH OFFER</span>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xl font-extrabold text-white">₹999</span>
                      <span className="text-xs text-slate-500 line-through">₹5,000</span>
                    </div>
                  </div>
                  <a 
                    href={CHECKOUT_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="hero-card-checkout-cta"
                    className="bg-[#D4AF37] hover:bg-[#E5C158] text-slate-950 font-bold px-4 py-2.5 rounded-lg text-xs transition-colors flex items-center gap-1 shrink-0"
                  >
                    Buy Now <ArrowRight className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* THE CLINICAL DILEMMA SECTION */}
      <section id="dilemma" className="py-16 sm:py-24 bg-[#111B31]/30 border-b border-[#182542]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
              The Practitioner's Struggle
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-serif">
              Why Selecting the Right Remedy is Only <span className="text-[#D4AF37]">50% of the Cure</span>.
            </h2>
            <p className="text-sm sm:text-base text-slate-400">
              You've spent hours analyzing the repertory, repertorized carefully, and selected the perfect simillimum. But without the correct potency and repetition, your medicine will fail to act.
            </p>
          </div>

          {/* Asymmetric Dilemma Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-stretch">
            
            {/* Left: The Confusion */}
            <div className="bg-slate-950/40 border border-red-500/20 p-6 sm:p-8 rounded-2xl space-y-6 flex flex-col justify-between">
              <div>
                <div className="inline-flex p-3 bg-red-950/40 text-red-400 rounded-xl border border-red-500/30 mb-4">
                  <AlertCircle className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-red-200 mb-2 font-serif">
                  The Confusion & Stagnant Outcomes
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-6">
                  Without structured posology, homeopathy practitioners fall into critical habits that halt deep therapeutic responses and cause loss of client trust.
                </p>

                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="h-2 w-2 rounded-full bg-red-500 mt-2 shrink-0"></span>
                    <p className="text-xs sm:text-sm text-slate-300">
                      <strong>Dilution Guesswork:</strong> Uncertain whether to give 30C or 200C in acute pediatric fever, resulting in delayed or zero action.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="h-2 w-2 rounded-full bg-red-500 mt-2 shrink-0"></span>
                    <p className="text-xs sm:text-sm text-slate-300">
                      <strong>Aggravation Panic:</strong> Prescribing high potencies (1M, 10M) blindly in patients with low vital force or organic damage, triggering massive systemic homeopathic aggravations.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="h-2 w-2 rounded-full bg-red-500 mt-2 shrink-0"></span>
                    <p className="text-xs sm:text-sm text-slate-300">
                      <strong>Repetition Blunders:</strong> Repeating constitutional dilutions too frequently, interrupting the curative action of the primary dose and leading to complex mixed states.
                    </p>
                  </li>
                </ul>
              </div>

              <div className="pt-4 border-t border-red-500/10 text-xs text-red-400/80 italic">
                * Real consequence: Stagnant chronic cases that force patients to seek chemical alternatives.
              </div>
            </div>

            {/* Right: The Potency Mastery */}
            <div className="bg-[#111B31] border border-[#D4AF37]/30 p-6 sm:p-8 rounded-2xl space-y-6 flex flex-col justify-between shadow-2xl relative">
              <div className="absolute top-4 right-4 bg-[#D4AF37]/10 text-[#D4AF37] px-2.5 py-0.5 rounded text-[10px] font-bold border border-[#D4AF37]/20">
                DR. RAJ'S PROTOCOL
              </div>
              <div>
                <div className="inline-flex p-3 bg-emerald-950/40 text-emerald-400 rounded-xl border border-emerald-500/30 mb-4">
                  <Sparkles className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-[#D4AF37] mb-2 font-serif">
                  The Confident Potency Mastery
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed mb-6">
                  Applying Dr. Raj Bondre's clinically-validated posology mathematical matrix lets you prescribe dilutions, tissue salts, and tinctures with bulletproof precision.
                </p>

                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="h-2 w-2 rounded-full bg-[#D4AF37] mt-2 shrink-0 animate-ping"></span>
                    <p className="text-xs sm:text-sm text-slate-200">
                      <strong>Scientific Posology:</strong> Understand how to mathematically analyze the patient's organic state to pick 6X vs 30C vs 1M instantly.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="h-2 w-2 rounded-full bg-[#D4AF37] mt-2 shrink-0"></span>
                    <p className="text-xs sm:text-sm text-slate-200">
                      <strong>Repetition Schedule:</strong> Follow strict, step-by-step algorithms on when to repeat, when to alternate cell salts, and when to completely pause.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="h-2 w-2 rounded-full bg-[#D4AF37] mt-2 shrink-0"></span>
                    <p className="text-xs sm:text-sm text-slate-200">
                      <strong>Aggravation Avoidance:</strong> Master Kent's observations to confidently manage patient reactions and prescribe dynamic placeholders.
                    </p>
                  </li>
                </ul>
              </div>

              <div className="pt-4 border-t border-[#D4AF37]/20">
                <button 
                  onClick={() => scrollToSection("potency-guide")}
                  className="w-full text-center text-xs text-[#D4AF37] hover:text-white bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/20 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-1"
                  data-testid="view-guide-btn"
                >
                  Interact with Potency Guide <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* CLINICAL AUTHENTICITY Section: Learn from Doctors with Real Patients */}
      <section id="clinical-proof" className="py-16 sm:py-24 border-b border-[#182542] bg-[#0A101D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-4xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
              Clinical Authenticity & Real Patient Practice
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-serif leading-tight">
              Learn from Doctors Who Have <span className="text-[#D4AF37]">Real Patients</span> — And Not Those Who Just Fake in Seminars.
            </h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto">
              Homeopathy is a practical, clinical science. Theories taught in expensive seminars often crumble in real daily practice. Dr. Raj Bondre teaches you the exact posology he uses to cure thousands of live patients in his own high-volume hospital.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Portrait YouTube Short - Real Patient Work */}
            <div className="lg:col-span-5 relative flex justify-center">
              <div className="absolute -inset-1 rounded-2xl bg-[#D4AF37]/10 blur-md animate-pulse"></div>
              <div className="relative w-full max-w-[340px] bg-[#111B31] border border-[#D4AF37]/30 p-4 rounded-2xl shadow-2xl space-y-3 text-center">
                <span className="block text-[10px] text-[#D4AF37] font-bold tracking-widest font-mono">
                  LIVE CLINICAL PROOF
                </span>
                
                {/* 9:16 portrait iframe */}
                <div className="relative aspect-[9/16] rounded-xl overflow-hidden border border-slate-800 bg-black">
                  <iframe
                    className="w-full h-full absolute inset-0"
                    src="https://www.youtube.com/embed/A_Wc9s_TmBw"
                    title="Dr. Raj Bondre Real Clinical Practice with Patients"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    data-testid="clinical-proof-shorts-iframe"
                  ></iframe>
                </div>

                <p className="text-xs text-slate-300 font-serif italic pt-1">
                  "Watch Dr. Raj Bondre consulting real patients and verifying therapeutic recoveries."
                </p>
              </div>
            </div>

            {/* Right: National Recognition, Sunil Gavaskar Award & Bestselling Author Showcase */}
            <div className="lg:col-span-7 space-y-8">
              
              {/* Card 1: Mr. Sunil Gavaskar Award & AYUSH Ministry Grid */}
              <div className="bg-[#111B31] border border-[#182542] rounded-2xl p-6 sm:p-8 space-y-6 shadow-xl">
                <div>
                  <span className="text-[10px] font-mono tracking-widest text-[#D4AF37] font-semibold uppercase">
                    NATIONAL RECOGNITION
                  </span>
                  <h3 className="text-lg sm:text-xl font-bold text-white font-serif mt-1">
                    Award-Winning Clinical Excellence
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-400 mt-2">
                    Dr. Raj's medical contributions are recognized at the highest level of Indian sports, culture, and governmental leadership.
                  </p>
                </div>

                {/* Images grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Sunil Gavaskar Award */}
                  <div className="space-y-2 border border-slate-800/60 p-3 rounded-xl bg-slate-950/40 hover:border-[#D4AF37]/20 transition-colors">
                    <div className="aspect-[4/3] rounded-lg overflow-hidden border border-slate-900 bg-black">
                      <img 
                        src="https://tagmango.com/assets/award-dr-raj-bondre-5740dd1499060dc5e9543ff2312b0ca2.png" 
                        alt="Dr Raj Bondre Awarded Best Homeopath of year By hands of Mr Sunil Gavaskar" 
                        className="w-full h-full object-cover"
                        data-testid="proof-image-sunil-gavaskar"
                      />
                    </div>
                    <span className="block text-[11px] font-bold text-white text-center leading-snug">
                      Best Homeopath of the Year
                    </span>
                    <p className="text-[10px] text-slate-400 text-center">
                      Presented by legendary Mr. Sunil Gavaskar.
                    </p>
                  </div>

                  {/* AYUSH Minister */}
                  <div className="space-y-2 border border-slate-800/60 p-3 rounded-xl bg-slate-950/40 hover:border-[#D4AF37]/20 transition-colors">
                    <div className="aspect-[4/3] rounded-lg overflow-hidden border border-slate-900 bg-black">
                      <img 
                        src="https://tagmango.com/assets/dr-raj-bondre-with-ayusj-minister-prataprao-jadhav-saheb-f0f410f6eaae8a4b60d20bb071c50dc2.jpg" 
                        alt="Dr Raj Bondre with Ayush Minister Prataprao Jadhav Saheb" 
                        className="w-full h-full object-cover"
                        data-testid="proof-image-ayush-minister"
                      />
                    </div>
                    <span className="block text-[11px] font-bold text-white text-center leading-snug">
                      Ministry Level Felicitation
                    </span>
                    <p className="text-[10px] text-slate-400 text-center">
                      With Honorable Union AYUSH Minister Shri Prataprao Jadhav.
                    </p>
                  </div>
                </div>
              </div>

              {/* Card 2: Bestselling Unlock Potency Book Cover Showcase */}
              <div className="bg-gradient-to-r from-[#111B31] to-[#182542] border border-[#D4AF37]/40 rounded-2xl p-6 sm:p-8 shadow-xl">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-6 items-center">
                  
                  {/* Book cover left */}
                  <div className="sm:col-span-4 relative flex justify-center">
                    <div className="absolute -inset-1 bg-[#D4AF37]/20 blur-md rounded-xl"></div>
                    <div className="relative w-full max-w-[130px] rounded-lg overflow-hidden border-2 border-[#D4AF37]/60 shadow-2xl">
                      <img 
                        src="https://tagmango.com/assets/front-potency-dr-raj-bondre-46a74b6053d657f35694422c88a32cb9.png" 
                        alt="Unlock Potency Mother Tincture Book Cover" 
                        className="w-full h-full object-cover"
                        data-testid="proof-image-potency-book"
                      />
                    </div>
                  </div>

                  {/* Book details right */}
                  <div className="sm:col-span-8 space-y-3 text-center sm:text-left">
                    <span className="text-[10px] font-mono tracking-widest text-[#D4AF37] font-bold uppercase">
                      BESTSELLING CLINICAL AUTHOR
                    </span>
                    <h4 className="text-lg font-bold text-white font-serif leading-snug">
                      Dr. Raj Bondre — Author of "Unlock Potency Book"
                    </h4>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      MD (Hom) and prestigious Gold Medalist. His bestselling reference manual, <strong>"Unlock Potency & Mother Tincture"</strong>, is widely used by BHMS graduates across the country as the gold standard for dynamic posology.
                    </p>
                    <div className="pt-2">
                      <a 
                        href={CHECKOUT_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 bg-[#D4AF37]/10 hover:bg-[#D4AF37] text-[#D4AF37] hover:text-slate-950 border border-[#D4AF37]/30 hover:border-[#D4AF37] px-4 py-2 rounded-lg text-xs font-bold transition-all duration-300"
                        data-testid="book-checkout-cta"
                      >
                        Claim Course + Posology Flowcharts <ArrowRight className="h-3 w-3" />
                      </a>
                    </div>
                  </div>

                </div>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* INTERACTIVE POTENCY DECISION TOOL SECTION */}
      <section id="potency-guide" className="py-16 sm:py-24 border-b border-[#182542]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
              Interactive Clinical Preview
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-serif">
              Mastering the Potency Scale Matrix
            </h2>
            <p className="text-sm sm:text-base text-slate-400">
              Click through the tabs below to preview the actual clinical rules taught inside our 10-hour masterclass. Never guess a dilution again.
            </p>
          </div>

          <div className="bg-[#111B31] border border-[#182542] rounded-2xl overflow-hidden shadow-2xl">
            
            {/* Tab Selection Headers */}
            <div className="grid grid-cols-2 md:grid-cols-4 border-b border-[#182542] bg-slate-950/40">
              {[
                { id: "6x-12x", label: "Biochemics (6X, 12X)", testId: "potency-tab-6x-12x" },
                { id: "30c-200c", label: "Dilutions (30, 200)", testId: "potency-tab-30c-200c" },
                { id: "1m-10m", label: "High Scale (1M, 10M)", testId: "potency-tab-1m-10m" },
                { id: "mother-tinctures", label: "Tinctures (Q)", testId: "potency-tab-q" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActivePotencyTab(tab.id)}
                  className={`py-4 px-4 text-xs sm:text-sm font-semibold transition-all text-center border-r border-[#182542] last:border-r-0 ${
                    activePotencyTab === tab.id
                      ? "bg-[#111B31] text-[#D4AF37] border-b-2 border-b-[#D4AF37]"
                      : "text-slate-400 hover:bg-[#182542]/40 hover:text-white"
                  }`}
                  data-testid={tab.testId}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Panel Content */}
            <div 
              className={`p-6 sm:p-8 bg-gradient-to-br ${POTENCY_DATA[activePotencyTab].color} transition-all duration-300`}
              data-testid="potency-card-content"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                
                <div className="lg:col-span-8 space-y-4">
                  <div>
                    <span className="text-[10px] uppercase font-mono tracking-widest text-[#D4AF37] font-semibold">
                      {POTENCY_DATA[activePotencyTab].subtitle}
                    </span>
                    <h3 className="text-xl sm:text-2xl font-bold text-white font-serif mt-1">
                      {POTENCY_DATA[activePotencyTab].title}
                    </h3>
                  </div>

                  <p className="text-sm text-slate-300 leading-relaxed">
                    {POTENCY_DATA[activePotencyTab].description}
                  </p>

                  <div className="space-y-2 pt-2">
                    <span className="block text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">
                      CLINICAL PROTOCOLS COVERED:
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {POTENCY_DATA[activePotencyTab].bullets.map((bullet, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs text-slate-300">
                          <CheckCircle2 className="h-4 w-4 text-[#D4AF37] shrink-0 mt-0.5" />
                          <span>{bullet}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Warning Alert Highlight box */}
                <div className="lg:col-span-4 bg-slate-950/80 border border-[#D4AF37]/30 p-5 rounded-xl space-y-3">
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-xs font-bold font-mono">CRITICAL PITFALL</span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    {POTENCY_DATA[activePotencyTab].alert}
                  </p>
                  <button 
                    onClick={() => scrollToSection("curriculum")}
                    className="w-full bg-[#D4AF37] hover:bg-[#E5C158] text-slate-950 font-bold py-2 px-4 rounded text-xs transition-colors flex items-center justify-center gap-1"
                    data-testid="potency-learn-syllabus"
                  >
                    Learn inside Course <ArrowRight className="h-3 w-3" />
                  </button>
                </div>

              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 10-HOUR CURRICULUM SYLLABUS SECTION */}
      <section id="curriculum" className="py-16 sm:py-24 bg-[#111B31]/30 border-b border-[#182542]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
              Syllabus Blueprint
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-serif">
              The 10 Recorded Lectures Curated for Practical Cures
            </h2>
            <p className="text-sm sm:text-base text-slate-400">
              10 hours of razor-focused, academic-level recorded videos. Walk away from textbook theories and master clinical posology practiced on thousands of real patients.
            </p>
          </div>

          {/* SYLLABUS ACCORDION LIST */}
          <div className="space-y-4" data-testid="syllabus-accordion-container">
            {SYLLABUS_MODULES.map((module, index) => {
              const isOpen = expandedModule === index;
              return (
                <div 
                  key={index}
                  className={`bg-[#111B31] border ${isOpen ? "border-[#D4AF37] shadow-[0_0_15px_rgba(212,175,55,0.1)]" : "border-[#182542]"} rounded-xl overflow-hidden transition-all duration-300`}
                >
                  
                  {/* Header/Trigger */}
                  <button
                    onClick={() => setExpandedModule(isOpen ? -1 : index)}
                    className="w-full flex items-center justify-between p-5 text-left transition-colors hover:bg-slate-950/20"
                    data-testid={`syllabus-accordion-trigger-${index + 1}`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-xs text-[#D4AF37] font-bold bg-[#D4AF37]/10 border border-[#D4AF37]/20 px-2 py-1 rounded">
                        LECTURE {module.num}
                      </span>
                      <div>
                        <h3 className="text-sm sm:text-base font-bold text-white leading-snug">
                          {module.title}
                        </h3>
                        <span className="text-[10px] text-slate-400 font-mono flex items-center gap-1 mt-0.5">
                          <Clock className="h-3 w-3 inline text-[#D4AF37]" /> {module.duration} Recorded
                        </span>
                      </div>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-300 shrink-0 ${isOpen ? "rotate-180 text-[#D4AF37]" : ""}`} />
                  </button>

                  {/* Expandable Panel */}
                  {isOpen && (
                    <div 
                      className="p-5 border-t border-[#182542] bg-[#0A101D]/40 space-y-4"
                      data-testid={`syllabus-accordion-content-${index + 1}`}
                    >
                      <p className="text-sm text-slate-300 leading-relaxed">
                        {module.desc}
                      </p>

                      <div className="space-y-2">
                        <span className="block text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">
                          LESSON TOPICS & CLINICAL PROTOCOLS:
                        </span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {module.details.map((detail, dIdx) => (
                            <div key={dIdx} className="flex items-center gap-2 text-xs text-slate-400">
                              <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]"></span>
                              <span>{detail}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              );
            })}
          </div>

          {/* Bottom syllabus CTA */}
          <div className="mt-12 bg-slate-950/50 border border-[#D4AF37]/20 rounded-2xl p-6 text-center space-y-4">
            <p className="text-sm text-slate-300 max-w-xl mx-auto">
              Ready to learn from Dr. Raj Bondre and eliminate your clinical posology doubts? Gain instant lifetime access to all 10 modules on the TagMango app.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href={CHECKOUT_URL}
                target="_blank"
                rel="noreferrer"
                className="w-full sm:w-auto text-center bg-[#D4AF37] hover:bg-[#E5C158] text-slate-950 font-extrabold px-6 py-3 rounded-lg text-sm transition-all shadow-md"
                data-testid="syllabus-enroll-cta"
              >
                Enroll Now in 10-Hour Masterclass @ ₹999
              </a>
              <button 
                onClick={() => scrollToSection("inquiry-form")}
                className="w-full sm:w-auto text-center bg-transparent border border-slate-700 text-slate-300 hover:text-white px-5 py-3 rounded-lg text-xs font-semibold transition-all"
                data-testid="syllabus-inquire-btn"
              >
                Get Syllabus PDF on WhatsApp
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* DR. RAJ BONDRE SHOWCASE INSTRUCTOR SECTION */}
      <section className="py-16 sm:py-24 border-b border-[#182542]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Instructor Portrait Image */}
            <div className="lg:col-span-5 relative">
              <div className="absolute -inset-1.5 rounded-2xl bg-[#D4AF37]/20 blur-md"></div>
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border-2 border-[#D4AF37]/40 shadow-2xl">
                <img 
                  src="https://tagmango.com/assets/dr-raj-bondre-with-ayusj-minister-prataprao-jadhav-saheb-f0f410f6eaae8a4b60d20bb071c50dc2.jpg" 
                  alt="Dr. Raj Bondre, Consulting Homeopath" 
                  className="w-full h-full object-cover"
                  data-testid="instructor-avatar-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-[#0A101D]/40 to-transparent"></div>
                <div className="absolute bottom-5 left-5 right-5 bg-slate-950/90 border border-slate-800 p-4 rounded-xl">
                  <span className="block text-[10px] text-[#D4AF37] font-bold uppercase tracking-wider">CHIEF INSTRUCTOR</span>
                  <span className="block text-lg font-bold text-white font-serif">Dr. Raj Bondre</span>
                  <p className="text-xs text-slate-400 mt-1">
                    Trained over 5,000+ homeopathic doctors & BHMS students in clinical potency mastery.
                  </p>
                </div>
              </div>
            </div>

            {/* Instructor Bios */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
                Meet Your Mentor
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-serif">
                A Practitioner Who Believes in Clinical <span className="text-[#D4AF37]">Prescribing Certainty</span>.
              </h2>
              
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                Dr. Raj Bondre is a senior homeopathic consultant and clinical researcher. Over his years of extensive practice, he discovered that the single greatest barrier holding homeopathic practitioners back from solving refractory cases was not remedy selection, but **faulty posology**.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-md mt-0.5">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white">5,000+ Doctor Alumni</h4>
                    <p className="text-xs text-slate-400">Guiding young homeopaths, BHMS scholars, and MD researchers globally to set up high-conversion clinics.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-md mt-0.5">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white">Clinically Proven Methodology</h4>
                    <p className="text-xs text-slate-400">Every single rule taught on dilutions (30C vs 200C) and highest potencies is illustrated with real case files and pathology sheets.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-md mt-0.5">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white">Practical Drainage Strategies</h4>
                    <p className="text-xs text-slate-400">Learn how to combine homeopathic tinctures with cellular Biochemics safely alongside deep systemic dynamic treatment.</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-[#182542] flex items-center gap-4">
                <a 
                  href={CHECKOUT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-[#D4AF37] hover:bg-[#E5C158] text-slate-950 font-extrabold px-6 py-3.5 rounded-lg text-xs transition-colors flex items-center gap-1.5"
                  data-testid="instructor-enroll-cta"
                >
                  Enroll Under Dr. Raj's Guidance <ArrowRight className="h-3.5 w-3.5" />
                </a>
                <span className="text-xs text-slate-400 font-mono">
                  ★ Rated 4.9/5 by 2100+ bhms practitioners
                </span>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* EMBEDDED VIDEO TESTIMONIALS SECTION */}
      <section id="testimonials" className="py-16 sm:py-24 bg-[#111B31]/30 border-b border-[#182542]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
              Cured Cases Reviews
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-serif">
              What Homeopaths & Students Say About Potency Mastery
            </h2>
            <p className="text-sm sm:text-base text-slate-400">
              Watch absolute, unedited feedback from actual doctors and BHMS scholars who transformed their homeopathy clinical results after applying these 10 hours of lectures.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* VIDEO PLAYER SIDE */}
            <div className="lg:col-span-7 space-y-4">
              <div className="relative aspect-video rounded-2xl overflow-hidden border border-[#D4AF37]/40 shadow-2xl bg-black">
                {isPlayingTestimonial ? (
                  <iframe
                    className="w-full h-full"
                    src={TESTIMONIAL_VIDEOS[activeVideoIndex].embedUrl}
                    title={TESTIMONIAL_VIDEOS[activeVideoIndex].title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    data-testid="testimonial-youtube-iframe"
                  ></iframe>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                    {/* Background apothecary art under play */}
                    <div className="absolute inset-0 opacity-40 z-0">
                      <img 
                        src="https://images.unsplash.com/photo-1638988562241-0e40dffe16ee?crop=entropy&cs=srgb&fm=jpg&q=85" 
                        alt="Homeopathy tincture bottles" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/70 mix-blend-multiply"></div>
                    </div>

                    <div className="relative z-10 space-y-4">
                      <button
                        onClick={() => setIsPlayingTestimonial(true)}
                        className="h-16 w-16 rounded-full bg-[#D4AF37] hover:bg-[#E5C158] text-slate-950 flex items-center justify-center shadow-lg transition-all duration-300 transform hover:scale-110"
                        data-testid="play-testimonial-overlay"
                        aria-label="Play video testimonial"
                      >
                        <Play className="h-7 w-7 fill-current ml-1 text-slate-950" />
                      </button>
                      <div>
                        <span className="block text-xs uppercase tracking-widest text-[#D4AF37] font-bold font-mono">
                          VIDEO FEEDBACK
                        </span>
                        <h3 className="text-lg font-bold text-white mt-1">
                          {TESTIMONIAL_VIDEOS[activeVideoIndex].title}
                        </h3>
                        <p className="text-xs text-slate-300 max-w-sm mx-auto mt-2">
                          "{TESTIMONIAL_VIDEOS[activeVideoIndex].highlight}"
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Doctor context details */}
              <div className="bg-[#111B31] border border-[#182542] p-5 rounded-xl flex items-start justify-between gap-4">
                <div>
                  <span className="text-xs text-[#D4AF37] font-bold uppercase tracking-wider">CURRENT SPEAKER</span>
                  <h4 className="text-base font-bold text-white mt-0.5">{TESTIMONIAL_VIDEOS[activeVideoIndex].doctor}</h4>
                  <p className="text-xs text-slate-400">{TESTIMONIAL_VIDEOS[activeVideoIndex].duration}</p>
                </div>
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs px-3 py-1.5 rounded-lg font-semibold font-mono">
                  100% Verified Cured Case
                </div>
              </div>
            </div>

            {/* SELECTION CAROUSEL SIDE */}
            <div className="lg:col-span-5 space-y-3.5">
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono mb-2">
                SELECT STUDENT REVIEW TO WATCH:
              </span>

              <div className="space-y-3">
                {TESTIMONIAL_VIDEOS.map((video, idx) => {
                  const isActive = activeVideoIndex === idx;
                  return (
                    <button
                      key={video.id}
                      onClick={() => {
                        setActiveVideoIndex(idx);
                        setIsPlayingTestimonial(false); // Reset to overlay on switch to let user trigger
                      }}
                      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 flex items-start gap-3.5 ${
                        isActive
                          ? "bg-[#111B31] border-[#D4AF37] shadow-lg"
                          : "bg-slate-950/30 border-[#182542] hover:bg-slate-950/50 hover:border-slate-700"
                      }`}
                      data-testid={`testimonial-video-${idx}`}
                    >
                      <div className={`p-2 rounded-lg shrink-0 mt-0.5 ${isActive ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-slate-800 text-slate-500"}`}>
                        <Play className="h-4 w-4" />
                      </div>
                      <div className="space-y-1">
                        <span className="block text-[10px] text-[#D4AF37] font-bold tracking-wider font-mono">
                          {video.doctor}
                        </span>
                        <h4 className="text-xs font-bold text-white line-clamp-1">
                          {video.title}
                        </h4>
                        <p className="text-[11px] text-slate-400 line-clamp-2 italic">
                          "{video.highlight}"
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="pt-2">
                <a 
                  href={CHECKOUT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="block text-center w-full bg-[#D4AF37]/10 hover:bg-[#D4AF37] text-[#D4AF37] hover:text-slate-950 border border-[#D4AF37]/30 hover:border-[#D4AF37] font-bold py-3.5 rounded-xl text-xs transition-all"
                  data-testid="testimonial-carousel-cta"
                >
                  Start Your Own Success Story @ ₹999
                </a>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* PRICING & IRRESISTIBLE OFFER CARD SECTION */}
      <section className="py-16 sm:py-24 border-b border-[#182542] relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-96 w-96 bg-[#D4AF37]/5 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
              Irresistible Offer
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-serif">
              Master Posology Now before the Price Rises
            </h2>
            <p className="text-sm sm:text-base text-slate-400">
              Stop letting incorrect potency selection halt your clinical outcomes. Get the entire 10 lectures recorded course with clinical cheat-sheets today.
            </p>
          </div>

          {/* Golden Masterclass Pricing Box */}
          <div className="relative">
            <div className="absolute -inset-1.5 rounded-2xl bg-gradient-to-r from-[#D4AF37] via-amber-500 to-[#D4AF37] opacity-25 blur-md"></div>
            
            <div className="relative bg-[#111B31] border-2 border-[#D4AF37] rounded-2xl overflow-hidden shadow-2xl p-6 sm:p-10">
              
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                
                {/* Left side info */}
                <div className="md:col-span-7 space-y-5">
                  <div className="inline-block px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 rounded-md text-xs font-bold font-mono">
                    80% ONE-TIME DISCOUNT LAUNCH
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white font-serif">
                    Potency Mastery Full Recorded Bundle
                  </h3>

                  <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                    Gain instant, permanent access to the complete 10 recorded lectures, downloadable clinical flowcharts, and the official completion certificate.
                  </p>

                  <div className="space-y-2.5">
                    <span className="block text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">
                      EVERYTHING ENROLLED PRACTITIONERS GET:
                    </span>
                    <ul className="space-y-2 text-xs text-slate-300">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4.5 w-4.5 text-[#D4AF37] shrink-0" />
                        <span>10 hours of practical posology recorded lectures (Valued @ ₹3,500)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4.5 w-4.5 text-[#D4AF37] shrink-0" />
                        <span>Downloadable Posology & Potency Decision flowcharts (Valued @ ₹999)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4.5 w-4.5 text-[#D4AF37] shrink-0" />
                        <span>Clinical cases repetition matrix indexes (Valued @ ₹999)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4.5 w-4.5 text-[#D4AF37] shrink-0" />
                        <span>Official Certificate of Academy Completion (Valued @ ₹500)</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Right side checkout card */}
                <div className="md:col-span-5 bg-slate-950/60 border border-[#182542] p-6 rounded-xl text-center space-y-5">
                  <div>
                    <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">TOTAL BUNDLE VALUE</span>
                    <span className="block text-slate-500 line-through text-lg font-medium mt-0.5">₹5,000+</span>
                  </div>

                  <div className="space-y-1">
                    <span className="text-[10px] text-[#D4AF37] uppercase tracking-widest font-bold font-mono">SPECIAL CLINICAL OFFER</span>
                    <div className="flex items-baseline justify-center gap-1.5">
                      <span className="text-4xl sm:text-5xl font-extrabold text-white">₹999</span>
                      <span className="text-xs text-slate-400">INR</span>
                    </div>
                    <span className="block text-[10px] text-emerald-400 font-bold font-mono">No monthly charges • Lifetime Access</span>
                  </div>

                  <div className="space-y-2 pt-1">
                    <a 
                      href={CHECKOUT_URL}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="pricing-enroll-cta"
                      className="block text-center w-full bg-[#D4AF37] hover:bg-[#E5C158] text-slate-950 font-extrabold py-3.5 rounded-lg text-sm transition-all duration-300 transform hover:scale-102 hover:shadow-[0_0_20px_rgba(212,175,55,0.2)]"
                    >
                      Instant Enrollment
                    </a>
                    <span className="block text-[10px] text-slate-400">
                      🔒 Secured transaction via TagMango checkout
                    </span>
                  </div>

                  <div className="pt-2 border-t border-slate-900 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                    <ShieldCheck className="h-4 w-4 text-[#D4AF37]" />
                    <span>TagMango 100% Verified Creator</span>
                  </div>
                </div>

              </div>

            </div>
          </div>

        </div>
      </section>

      {/* SYLLABUS REGISTRATION / CLINICAL INQUIRY FORM */}
      <section id="inquiry-form" className="py-16 sm:py-24 bg-[#111B31]/30 border-b border-[#182542]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="bg-[#111B31] border border-[#182542] rounded-2xl p-6 sm:p-10 space-y-6 shadow-xl">
            <div className="text-center space-y-2">
              <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#D4AF37]">
                Clinical Counseling Support
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-white font-serif">
                Have Doubts? Get a Callback & Syllabus PDF
              </h3>
              <p className="text-xs sm:text-sm text-slate-300 max-w-lg mx-auto">
                Fill out the registration details below to receive the detailed 10-module academic brochure on WhatsApp, or speak directly to our admissions team.
              </p>
            </div>

            {inquirySubmitted ? (
              <div className="p-6 bg-slate-950/50 border border-[#D4AF37]/30 rounded-xl text-center space-y-3" data-testid="inquiry-success-message">
                <div className="inline-flex p-3 bg-emerald-950/40 text-emerald-400 rounded-full border border-emerald-500/30">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h4 className="text-base font-bold text-white">Admissions Registration Successful!</h4>
                <p className="text-xs text-slate-300 max-w-sm mx-auto">
                  Thank you, <strong className="text-[#D4AF37]">{inquiryName}</strong>. We've sent the detailed 10 lectures booklet to <strong className="text-white">{inquiryPhone}</strong> and our team will reach out within 2 hours.
                </p>
                <button
                  onClick={() => setInquirySubmitted(false)}
                  className="text-xs text-[#D4AF37] hover:underline font-mono"
                >
                  Change registration info
                </button>
              </div>
            ) : (
              <form onSubmit={handleInquirySubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono" htmlFor="name">
                      Doctor / Student Name
                    </label>
                    <input 
                      type="text" 
                      id="name"
                      placeholder="e.g. Dr. Rajeev Patel"
                      value={inquiryName}
                      onChange={(e) => setInquiryName(e.target.value)}
                      className="w-full bg-slate-950/60 border border-[#182542] focus:border-[#D4AF37] rounded-lg px-4 py-3 text-xs text-white placeholder-slate-500 outline-none transition-all"
                      data-testid="inquiry-name"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono" htmlFor="email">
                      Academic Email Address
                    </label>
                    <input 
                      type="email" 
                      id="email"
                      placeholder="e.g. rajeev.patel@gmail.com"
                      value={inquiryEmail}
                      onChange={(e) => setInquiryEmail(e.target.value)}
                      className="w-full bg-slate-950/60 border border-[#182542] focus:border-[#D4AF37] rounded-lg px-4 py-3 text-xs text-white placeholder-slate-500 outline-none transition-all"
                      data-testid="inquiry-email"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono" htmlFor="phone">
                    WhatsApp Contact Number (For Syllabus PDF & Support)
                  </label>
                  <input 
                    type="tel" 
                    id="phone"
                    placeholder="e.g. +91 98765 43210"
                    value={inquiryPhone}
                    onChange={(e) => setInquiryPhone(e.target.value)}
                    className="w-full bg-slate-950/60 border border-[#182542] focus:border-[#D4AF37] rounded-lg px-4 py-3 text-xs text-white placeholder-slate-500 outline-none transition-all"
                    data-testid="inquiry-phone"
                    required
                  />
                </div>

                <button 
                  type="submit"
                  className="w-full bg-[#D4AF37] hover:bg-[#E5C158] text-slate-950 font-extrabold py-3.5 rounded-lg text-xs transition-colors flex items-center justify-center gap-1.5"
                  data-testid="inquiry-submit"
                >
                  <Send className="h-4 w-4 text-slate-950" /> Register & Receive Syllabus Booklet on WhatsApp
                </button>
              </form>
            )}

            <div className="pt-2 text-center text-[10px] text-slate-400 font-mono flex items-center justify-center gap-1.5">
              <span>🛡 We respect doctor confidentiality. No spam.</span>
            </div>
          </div>

        </div>
      </section>

      {/* FAQ SECTION */}
      <section id="faq" className="py-16 sm:py-24 border-b border-[#182542]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
              Frequently Answered
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-serif">
              Posology Academic Doubt Resolution
            </h2>
            <p className="text-sm sm:text-base text-slate-400">
              Before enrolling, read through our answers to the most common questions raised by BHMS practitioners, MD interns, and clinical doctors.
            </p>
          </div>

          <div className="space-y-4" data-testid="faq-container">
            {[
              {
                q: "Is this course suitable for first-year BHMS students or only senior doctors?",
                a: "It is perfect for both! Dr. Raj Bondre starts from the absolute fundamental definition of susceptibility and dynamic planes (Module 1) before scaling to complex chronic multi-remedy intercurrent blocks (Module 8). Both students and 20-year veteran practitioners will find massive clinical utility."
              },
              {
                q: "How long will I have access to these 10 recorded lectures?",
                a: "You get permanent Lifetime Access! Once you enroll on our TagMango page, all 10 hours of video lectures, the biochemic combinatory charts, and posology blueprints are permanently unlocked in your personal learner profile."
              },
              {
                q: "Can I watch these lectures on my mobile device?",
                a: "Absolutely! The course runs beautifully on the official TagMango Mobile App (available on iOS and Android) as well as any desktop web browser."
              },
              {
                q: "Will I receive a course completion certificate?",
                a: "Yes! Once you complete viewing all 10 recorded lectures, an official Certificate of Posology Mastery signed by Dr. Raj Bondre will be automatically generated and sent to your registered email."
              },
              {
                q: "Who should I reach out to if I face issues during checkout?",
                a: "All transactions are secured and managed directly by the TagMango creator payment gateway. For support, you can reach out via email, WhatsApp, or through your built-in Student Studio chat."
              }
            ].map((faq, fIdx) => (
              <div 
                key={fIdx}
                className="bg-[#111B31] border border-[#182542] rounded-xl p-5 sm:p-6 space-y-2"
              >
                <div className="flex gap-2.5">
                  <HelpCircle className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                  <h4 className="text-sm sm:text-base font-bold text-white font-serif">
                    {faq.q}
                  </h4>
                </div>
                <div className="pl-7">
                  <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* THE MANDATORY P.S. SECTION */}
      <section className="py-16 sm:py-24 bg-[#111B31] border-b border-[#182542]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div 
            className="border-2 border-dashed border-[#D4AF37] rounded-2xl p-6 sm:p-10 space-y-6 bg-slate-950/40 relative overflow-hidden"
            data-testid="bottom-ps-block"
          >
            {/* Background design accents */}
            <div className="absolute top-0 right-0 h-16 w-16 bg-[#D4AF37]/10 rounded-full blur-md"></div>
            
            <div className="flex items-start gap-4">
              <div className="p-2.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded-xl border border-[#D4AF37]/30 mt-1 shrink-0">
                <Flame className="h-6 w-6 animate-bounce" />
              </div>
              
              <div className="space-y-4">
                <span className="text-xs font-bold font-mono tracking-widest text-[#D4AF37] uppercase">
                  A FINAL MESSAGE FROM DR. RAJ BONDRE
                </span>
                
                <h3 className="text-lg sm:text-xl font-bold text-white font-serif leading-snug">
                  P.S. The Clinical Value of Correct Posology Exceeds Thousands of Rupees.
                </h3>
                
                <div className="space-y-3 text-xs sm:text-sm text-slate-300 leading-relaxed">
                  <p>
                    The actual real-world clinical value of this 10-hour recorded posology masterclass exceeds <strong className="text-[#D4AF37]">₹5,000</strong>. Yet, to empower every practicing homeopath, young MD intern, and struggling BHMS student globally, we have decided to keep the price at an absolute minimum of <strong className="text-white bg-[#D4AF37]/20 border border-[#D4AF37]/30 px-1.5 py-0.5 rounded font-mono font-bold">₹999 Only</strong>.
                  </p>
                  <p>
                    Please understand: remedy selection is only half the battle. Your clinic results and patient cured rate live and die by your potency selections and repetitions. Do not let incorrect repetitions or dynamic dilutions hold your clinical outcomes back!
                  </p>
                  <p className="font-semibold text-white">
                    Claim this irresistible ₹999 launch offer today before the price returns to ₹5,000.
                  </p>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <a 
                    href={CHECKOUT_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="text-center bg-[#D4AF37] hover:bg-[#E5C158] text-slate-950 font-extrabold px-8 py-4 rounded-xl text-sm transition-all duration-300 shadow-lg transform hover:-translate-y-0.5"
                    data-testid="bottom-ps-enroll-cta"
                  >
                    Enroll & Master Potencies Now @ ₹999
                  </a>
                  <button 
                    onClick={() => scrollToSection("curriculum")}
                    className="text-center text-xs text-slate-400 hover:text-white underline font-mono"
                    data-testid="bottom-ps-view-syllabus"
                  >
                    Read curriculum breakdown
                  </button>
                </div>

              </div>
            </div>

          </div>

        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-950 text-slate-500 py-12 border-t border-[#182542]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            
            {/* Logo and Brand Info */}
            <div className="md:col-span-4 space-y-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-[#D4AF37] flex items-center justify-center p-0.5">
                  <Stethoscope className="h-4.5 w-4.5 text-slate-950" />
                </div>
                <div>
                  <span className="block text-base font-bold text-white font-serif">Dr. Raj Bondre</span>
                  <span className="block text-[9px] text-[#D4AF37] tracking-wider uppercase font-semibold">Homeopathy Studio</span>
                </div>
              </div>
              <p className="text-xs text-slate-400 max-w-sm">
                Empowering BHMS students, clinical homeopaths, and medical graduates around the world with mathematically validated potency selection and repetition guidelines.
              </p>
            </div>

            {/* Quick Links */}
            <div className="md:col-span-4 space-y-2">
              <span className="block text-xs font-bold text-white uppercase tracking-wider font-mono">FUNNEL NAVIGATION</span>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <button onClick={() => scrollToSection("hero")} className="text-left hover:text-white transition-colors">Hero Section</button>
                <button onClick={() => scrollToSection("dilemma")} className="text-left hover:text-white transition-colors">The Dilemma</button>
                <button onClick={() => scrollToSection("potency-guide")} className="text-left hover:text-white transition-colors">Potency Guide</button>
                <button onClick={() => scrollToSection("curriculum")} className="text-left hover:text-white transition-colors">Lectures Syllabus</button>
                <button onClick={() => scrollToSection("testimonials")} className="text-left hover:text-white transition-colors">Reviews Grid</button>
                <button onClick={() => scrollToSection("faq")} className="text-left hover:text-white transition-colors">FAQ Accordion</button>
              </div>
            </div>

            {/* Legal Support details */}
            <div className="md:col-span-4 space-y-3 text-xs">
              <span className="block text-xs font-bold text-white uppercase tracking-wider font-mono">STUDIO SUPPORT</span>
              <p className="text-slate-400">
                Dr. Raj Bondre's Potency Mastery masterclass is officially run and authenticated on the TagMango Creator Studio Platform.
              </p>
              <div className="text-slate-400 space-y-1">
                <span className="block">📧 Support: <strong className="text-slate-300">support@homeomonk.com</strong></span>
                <span className="block">🌐 Canonical: <strong className="text-slate-300">learn.homeomonk.com</strong></span>
              </div>
            </div>

          </div>

          <div className="pt-8 border-t border-[#182542] flex flex-col sm:flex-row items-center justify-between gap-4 text-[10px] text-slate-500 font-mono">
            <span>
              &copy; 2026 Dr. Raj Bondre. All Rights Reserved. Mango AI Funnel Builder.
            </span>
            <div className="flex gap-4">
              <a href="#faq" className="hover:text-white">Terms of Enrollment</a>
              <a href="#faq" className="hover:text-white">Privacy Policy</a>
              <a href="#faq" className="hover:text-white">TagMango Refund Policy</a>
            </div>
          </div>

        </div>
      </footer>

    </div>
  );
}